import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TrendingUp, PoundSterling, ShoppingCart, Activity, ArrowUpRight, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * ROI widget (iter 60) — *real* outcome maths from buyer postbacks. No predictions.
 *
 * Why this beats LeadByte/LeadProsper:
 *  - CPA, not just ROAS — the metric finance teams actually budget against
 *  - Effective CPL (post-returns) — surfaces the *real* lead cost, not the sticker
 *  - Cohort table by source_sub_id with a one-click "Scale this source" CTA
 *  - Empty state with copyable postback `curl` — never shows fake numbers
 */
export default function RoiWidget() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);
  const [busy, setBusy] = useState(null); // currently scaling row id
  const [priority, setPriority] = useState([]); // [{publisher_id, source_sub_id}]

  const refreshPriority = () =>
    api.get("/client/preferences").then((r) => setPriority(r.data?.priority_sources || [])).catch(() => {});
  const load = (days) => api.get(`/client/roi?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(period); refreshPriority(); }, [period]);

  const isPrioritised = (row) =>
    priority.some((p) => p.publisher_id === row.publisher_id && (p.source_sub_id || "") === (row.source_sub_id === "(none)" ? "" : row.source_sub_id));

  const scaleSource = async (row, action = "scale") => {
    const id = `${row.publisher_id || "n"}|${row.source_sub_id}`;
    setBusy(id);
    try {
      await api.post(`/client/sources/${row.publisher_id || "none"}/scale`, {
        source_sub_id: row.source_sub_id === "(none)" ? "" : row.source_sub_id,
        action,
      });
      await refreshPriority();
      toast.success(action === "scale" ? "Source prioritised — boosted in routing" : "Removed from priority list");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return null;
  const p = data.portfolio;

  // Empty state — no postbacks yet
  if (!p.has_postbacks) {
    return (
      <Card className="rounded-xl shadow-soft border-slate-200" data-testid="roi-empty">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <span className="inline-grid place-items-center size-9 rounded-md bg-[#0055FF]/10 text-[#0055FF] shrink-0"><Sparkles className="size-4" /></span>
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] font-semibold text-slate-900">Unlock real ROI tracking</h3>
              <p className="mt-1 text-[13px] text-slate-600 max-w-2xl">
                Postback each sale from your CRM and we'll show you actual CPA, ROAS and per-source A/B breakdowns —
                no estimates, no predictions, just your numbers.
              </p>
              <pre className="mt-3 text-[10.5px] font-mono leading-relaxed overflow-x-auto bg-slate-900 text-slate-100 rounded-lg p-3.5 whitespace-pre">{POSTBACK_CURL}</pre>
              <button
                className="mt-2 text-[12px] text-[#0055FF] inline-flex items-center gap-1 hover:underline"
                onClick={() => { navigator.clipboard.writeText(POSTBACK_CURL); toast.success("Copied postback curl"); }}
              ><Copy className="size-3" /> Copy curl</button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Live state — buyer has postback data
  return (
    <Card className="rounded-xl shadow-soft border-slate-200" data-testid="roi-widget">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="inline-grid place-items-center size-7 rounded-md bg-[#0055FF]/10 text-[#0055FF]"><TrendingUp className="size-3.5" /></span>
            <h3 className="font-medium text-slate-900">Outcome ROI</h3>
            <span className="text-[11px] text-slate-500">From your CRM postbacks · {data.days}d</span>
          </div>
          <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 text-[11px]" data-testid="roi-period">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setPeriod(d)}
                className={`px-2.5 py-1 rounded-full transition-colors ${period === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                data-testid={`roi-period-${d}`}>{d}d</button>
            ))}
          </div>
        </div>

        {/* 4 KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-b border-slate-100">
          <Tile Icon={ShoppingCart}    label="Sales closed"   value={p.sold.toLocaleString()} sub={`${p.accepted} accepted`} />
          <Tile Icon={PoundSterling}   label="Revenue"        value={`£${p.revenue_gbp.toLocaleString(undefined, {maximumFractionDigits:0})}`} sub={`from ${p.sold} sales`} />
          <Tile Icon={Activity}        label="CPA"            value={p.cpa_gbp != null ? `£${p.cpa_gbp.toLocaleString(undefined, {maximumFractionDigits:0})}` : "—"} sub={`per sold lead`} highlight />
          <Tile Icon={TrendingUp}      label="ROAS"           value={p.roas != null ? `${p.roas.toFixed(2)}×` : "—"} sub={`revenue ÷ spend`} highlight />
        </div>

        {/* Effective CPL banner */}
        <div className="px-5 py-3 bg-slate-50/60 border-b border-slate-100 text-[12.5px] flex items-center flex-wrap gap-x-4 gap-y-1">
          <span className="text-slate-600">Sticker spend <span className="font-mono tabular-nums text-slate-900">£{p.spend_gbp.toLocaleString(undefined, {maximumFractionDigits:0})}</span></span>
          <span className="text-slate-400">−</span>
          <span className="text-slate-600">Refunds <span className="font-mono tabular-nums text-emerald-700">£{p.returns_refunded_gbp.toLocaleString(undefined, {maximumFractionDigits:0})}</span></span>
          <span className="text-slate-400">=</span>
          <span className="text-slate-700">Effective CPL <span className="font-mono tabular-nums text-slate-900 font-medium">£{p.effective_cpl_gbp ?? "—"}</span></span>
        </div>

        {/* Cohort table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="roi-cohorts">
            <thead className="bg-white">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 text-left border-b border-slate-100">
                <th className="py-3 px-5 font-medium">Source</th>
                <th className="font-medium text-right">Accepted</th>
                <th className="font-medium text-right">Sold</th>
                <th className="font-medium text-right">Sold rate</th>
                <th className="font-medium text-right">CPA</th>
                <th className="font-medium text-right">Spend</th>
                <th className="font-medium text-right pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {data.by_source.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-[12.5px] text-slate-500">No cohorts in this window yet.</td></tr>
              )}
              {data.by_source.slice(0, 10).map((row, i) => {
                const rowKey = `${row.publisher_id || "n"}|${row.source_sub_id}`;
                const isBusy = busy === rowKey;
                const isWinner = row.sold > 0 && (row.sold_rate >= 0.05);
                const prioritised = isPrioritised(row);
                return (
                  <tr key={rowKey} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`roi-row-${i}`}>
                    <td className="py-3 px-5 font-mono text-[12px] text-slate-800 flex items-center gap-2">
                      {row.source_sub_id}
                      {prioritised && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#0055FF] bg-[#0055FF]/10 px-1.5 py-0.5 rounded-full">Prioritised</span>}
                    </td>
                    <td className="py-3 text-right tabular-nums text-slate-700">{row.accepted}</td>
                    <td className="py-3 text-right tabular-nums text-slate-700">{row.sold}</td>
                    <td className="py-3 text-right tabular-nums">
                      <span className={`text-[12px] px-2 py-0.5 rounded-full ${isWinner ? "bg-emerald-50 text-emerald-700" : row.sold_rate > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {(row.sold_rate * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 text-right tabular-nums text-slate-900 font-medium">{row.cpa_gbp != null ? `£${row.cpa_gbp.toLocaleString(undefined, {maximumFractionDigits:0})}` : "—"}</td>
                    <td className="py-3 text-right tabular-nums text-slate-700">£{row.spend_gbp.toLocaleString(undefined, {maximumFractionDigits:0})}</td>
                    <td className="py-3 pr-5 text-right">
                      {row.publisher_id && (isWinner || prioritised) && (
                        <Button size="sm" variant={prioritised ? "ghost" : "outline"} disabled={isBusy}
                                onClick={() => scaleSource(row, prioritised ? "unscale" : "scale")}
                                className="rounded-full h-7 text-[11.5px]" data-testid={`roi-scale-${i}`}>
                          {isBusy ? "…" : prioritised ? "Unprioritise" : <>Scale <ArrowUpRight className="size-3" /></>}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 text-[11px] text-slate-500 border-t border-slate-100">
          Tap <span className="text-slate-700 font-medium">Scale</span> on a winning cohort to mark its publisher/source as priority — surfaced in admin routing decisions. Real outcomes only, no predictions.
        </div>
      </CardContent>
    </Card>
  );
}

function Tile({ Icon, label, value, sub, highlight = false }) {
  return (
    <div className={`p-5 border-r border-slate-100 last:border-r-0 ${highlight ? "bg-[#0055FF]/[0.025]" : ""}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500">
        <Icon className="size-3" /> {label}
      </div>
      <div className={`mt-1.5 text-2xl font-semibold tabular-nums ${highlight ? "text-[#0055FF]" : "text-slate-900"}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

const POSTBACK_CURL = `curl -X POST https://connex.is/api/v1/leads/{lead_id}/postback \\
  -H "X-Postback-Token: <token from delivered lead>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "status": "sold",
    "sale_value_gbp": 1200,
    "notes": "closed at 4.6%"
  }'`;
